import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Hash,
  Bell,
  RefreshCw,
  Settings,
  ArrowLeft,
  Users,
  CheckCircle,
  Send,
  Megaphone,
  Volume2,
  VolumeX,
  Bot,
  Server,
  MessageCircle,
  Copy
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

// Notification types
const DISCORD_NOTIFICATIONS = [
  { id: 'new_application', name: 'New Applications', description: 'Post when candidates apply', default: true },
  { id: 'interviews', name: 'Interview Updates', description: 'Interview scheduling notifications', default: true },
  { id: 'offers', name: 'Offer Updates', description: 'Offer sent/accepted notifications', default: true },
  { id: 'announcements', name: 'Announcements', description: 'Team announcements', default: false },
];

const DiscordIntegration = ({ onBack }) => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [server, setServer] = useState(null);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [notifications, setNotifications] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [showSendMessage, setShowSendMessage] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [recentMessages, setRecentMessages] = useState([]);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/discord/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnected(response.data.connected);
      setServer(response.data.server);
      setChannels(response.data.channels || []);
      setNotifications(response.data.notifications || {});
      if (response.data.connected) {
        fetchRecentMessages();
      }
    } catch (error) {
      console.error('Error checking Discord connection:', error);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/discord/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentMessages(response.data.messages || []);
    } catch (error) {
      // Demo data
      setRecentMessages([
        { id: '1', content: '📋 New application: John Doe - Software Engineer', channel: 'recruitment', time: new Date(Date.now() - 300000).toISOString() },
        { id: '2', content: '📅 Interview scheduled: Sarah @ 2PM tomorrow', channel: 'interviews', time: new Date(Date.now() - 600000).toISOString() },
        { id: '3', content: '🎉 Offer accepted by Mike Johnson!', channel: 'recruitment', time: new Date(Date.now() - 900000).toISOString() },
      ]);
    }
  };

  const handleConnect = async () => {
    if (!webhookUrl.trim()) {
      toast.error('Please enter a webhook URL');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/integrations/discord/connect`, {
        webhook_url: webhookUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Discord connected successfully');
      setConnected(true);
      setServer(response.data.server);
      setChannels(response.data.channels || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Discord');
    }
  };

  const handleOAuthConnect = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/integrations/discord/oauth-connect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.oauth_url) {
        window.location.href = response.data.oauth_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Discord');
    }
  };

  const handleDisconnect = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/discord/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Discord disconnected');
      setConnected(false);
      setServer(null);
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedChannel) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/discord/send`, {
        channel_id: selectedChannel,
        content: messageText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Message sent');
      setMessageText('');
      setShowSendMessage(false);
      fetchRecentMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateNotifications = async (notifId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/integrations/discord/notifications`, {
        [notifId]: enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => ({ ...prev, [notifId]: enabled }));
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const formatTime = (ts) => {
    const date = new Date(ts);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to App Hub
        </Button>
        
        <Card className="max-w-lg mx-auto">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
              <Hash className="h-8 w-8 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Connect Discord</h2>
            <p className="text-slate-500 mb-6">
              Send recruitment notifications to your Discord server and manage team communication.
            </p>

            <div className="space-y-4 text-left">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Bot className="h-4 w-4" />
                  Option 1: Webhook (Recommended)
                </h4>
                <ol className="space-y-1 text-sm text-slate-600 list-decimal list-inside mb-3">
                  <li>Go to your Discord server settings</li>
                  <li>Navigate to Integrations &gt; Webhooks</li>
                  <li>Create a new webhook for your channel</li>
                  <li>Copy the webhook URL and paste below</li>
                </ol>
                <Input
                  placeholder="https://discord.com/api/webhooks/..."
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  className="mb-2"
                />
                <Button onClick={handleConnect} className="w-full bg-indigo-600 hover:bg-indigo-700">
                  Connect with Webhook
                </Button>
              </div>

              <div className="text-center text-sm text-slate-500">or</div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Server className="h-4 w-4" />
                  Option 2: Bot (Full Access)
                </h4>
                <p className="text-sm text-slate-600 mb-3">
                  Add our Discord bot to your server for full integration features.
                </p>
                <Button variant="outline" onClick={handleOAuthConnect} className="w-full">
                  <Hash className="h-4 w-4 mr-2" />
                  Add Bot to Server
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="discord-integration">
      {/* Header */}
      <div className="p-4 border-b bg-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
            <Hash className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-semibold text-slate-800">Discord</h1>
            <p className="text-xs text-slate-500">{server?.name || 'Connected'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowSettings(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button onClick={() => setShowSendMessage(true)}>
            <Send className="h-4 w-4 mr-2" />
            Send Message
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Server Info */}
        <div className="w-72 border-r bg-slate-50 p-4">
          <div className="mb-6">
            <h3 className="text-xs font-semibold uppercase text-slate-500 mb-3">Server</h3>
            <div className="p-3 bg-white rounded-lg border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Server className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-700">{server?.name || 'My Server'}</p>
                  <p className="text-xs text-slate-500">{channels.length} channels</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase text-slate-500 mb-3">Channels</h3>
            <div className="space-y-1">
              {channels.length > 0 ? channels.map(channel => (
                <div
                  key={channel.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-white cursor-pointer"
                >
                  <Hash className="h-4 w-4 text-slate-400" />
                  <span>{channel.name}</span>
                </div>
              )) : (
                <>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600">
                    <Hash className="h-4 w-4 text-slate-400" />
                    <span>recruitment</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600">
                    <Hash className="h-4 w-4 text-slate-400" />
                    <span>interviews</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-600">
                    <Hash className="h-4 w-4 text-slate-400" />
                    <span>announcements</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Recent Messages */}
        <div className="flex-1 flex flex-col">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-slate-700">Recent Notifications Sent</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {recentMessages.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No messages sent yet</p>
              </div>
            ) : (
              recentMessages.map(msg => (
                <div key={msg.id} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-slate-500">#{msg.channel}</span>
                        <span className="text-xs text-slate-400">{formatTime(msg.time)}</span>
                      </div>
                      <p className="text-slate-700">{msg.content}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Send Message Dialog */}
      <Dialog open={showSendMessage} onOpenChange={setShowSendMessage}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send Message
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Channel</label>
              <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                <SelectTrigger>
                  <SelectValue placeholder="Select channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recruitment">#recruitment</SelectItem>
                  <SelectItem value="interviews">#interviews</SelectItem>
                  <SelectItem value="announcements">#announcements</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Message</label>
              <Textarea
                placeholder="Type your message..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendMessage(false)}>Cancel</Button>
            <Button onClick={handleSendMessage} disabled={sending || !messageText.trim() || !selectedChannel}>
              {sending ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Discord Notifications
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {DISCORD_NOTIFICATIONS.map(notif => (
              <div key={notif.id} className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-slate-700">{notif.name}</p>
                  <p className="text-xs text-slate-500">{notif.description}</p>
                </div>
                <Switch
                  checked={notifications[notif.id] ?? notif.default}
                  onCheckedChange={(checked) => handleUpdateNotifications(notif.id, checked)}
                />
              </div>
            ))}
          </div>
          <DialogFooter className="flex justify-between">
            <Button variant="destructive" onClick={handleDisconnect}>
              Disconnect
            </Button>
            <Button onClick={() => setShowSettings(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscordIntegration;
